type MediaRecord = {
  id?: string;
  mediaType?: 'image' | 'video';
  usage?: string | string[];
  cloudinaryURL?: string;
};

type VariantImageSource =
  | string[]
  | {
      main?: string;
      model?: string;
      zoom?: string;
    };

const USAGE_ORDER: string[] = ['product-main', 'product-secondary', 'product-third', 'product-extra'];

function getUsageList(m: MediaRecord): string[] {
  if (Array.isArray(m.usage)) return m.usage;
  if (m.usage) return [m.usage];
  return [];
}

/**
 * Returns product media ordered by usage priority: main → secondary → third → extra.
 * Media with no recognized usage is appended at the end.
 * Both images and videos are sorted by their usage tag — no special video treatment.
 */
export function orderProductMedia(media: MediaRecord[]): MediaRecord[] {
  const withUrl = media.filter(m => m.cloudinaryURL);

  return [...withUrl].sort((a, b) => {
    const aUsages = getUsageList(a);
    const bUsages = getUsageList(b);
    const aIndex = USAGE_ORDER.findIndex(u => aUsages.includes(u));
    const bIndex = USAGE_ORDER.findIndex(u => bUsages.includes(u));
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });
}

/**
 * Pick media URL by usage field only.
 * Returns undefined if no media has the requested usage — no fallbacks.
 */
export function pickProductMediaUrl(media: MediaRecord[], _hints: string[], usage?: string): string | undefined {
  if (!media.length) return undefined;

  if (usage) {
    const match = media.find((item) => {
      if (item.mediaType !== 'image') return false;
      const usages = getUsageList(item);
      return usages.includes(usage);
    });
    return match?.cloudinaryURL;
  }

  // No usage specified — return first image (used by listing pages for thumbnails)
  return media.find((item) => item.mediaType === 'image')?.cloudinaryURL;
}

export function pickVariantImageUrl(images: VariantImageSource | undefined, index: number): string | undefined {
  if (!images) return undefined;
  if (Array.isArray(images)) {
    return images[index];
  }
  if (index === 0) return images.main;
  if (index === 1) return images.model;
  if (index === 2) return images.zoom;
  return undefined;
}
